// Modal dialog — ported from legacy openModal/closeModal semantics:
//  - focus is moved into the dialog on open (unless a caller already focused
//    a field) and restored to the trigger on close;
//  - Tab is trapped inside the dialog (trapStep below, pure + tested);
//  - Escape closes (wired by main.js via closeModal — idempotent when shut);
//  - KEEP-OPEN-ON-FAIL contract: a failed async save calls setBtnBusy(false)
//    and returns WITHOUT closeModal(), so typed input is never destroyed;
//    success paths just closeModal().
// Differences from legacy: footer actions are real functions (no inline
// onclick strings — CSP), and the host elements are created lazily.

/**
 * Pure focus-trap step. Given the Tab direction, the index of the currently
 * focused element within the dialog's focusable list (-1 when focus is
 * outside the dialog), and the list length, return the index to force focus
 * to — or null to let the browser's default Tab order proceed.
 * Semantics ported from legacy trapModalTab():
 *  - no focusables → -2 (sentinel: focus the dialog itself);
 *  - Shift+Tab on the first element (or from outside) → last;
 *  - Tab on the last element → first;
 *  - otherwise → null (default browser move stays inside the dialog).
 * @param {boolean} shiftKey
 * @param {number} activeIndex index in focusables, or -1 if outside the dialog
 * @param {number} count number of focusable elements
 * @returns {number|null}
 */
export function trapStep(shiftKey, activeIndex, count) {
  if (count <= 0) return -2;
  if (shiftKey) {
    if (activeIndex === 0 || activeIndex === -1) return count - 1;
    return null;
  }
  if (activeIndex === count - 1) return 0;
  return null;
}

// Exported: the mobile nav drawer traps Tab with the SAME rules and the same
// selector. Two definitions of "focusable" drift, and the one that drifts is
// always the one nobody is looking at.
export const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/** @type {Element|null} */
let _prevFocus = null;
/** @type {((ev: KeyboardEvent) => void)|null} */
let _trapHandler = null;

/**
 * The elements a modal must take out of the page while it is open: the app
 * shell and the patient-workspace overlay (which lives on <body>, outside
 * #app). Toasts and the idle-logout banner are deliberately NOT here — the
 * "Stay signed in" button must keep working above any dialog.
 * @param {any} doc
 * @returns {any[]}
 */
function inertTargets(doc) {
  return ['app', 'ws-overlay'].map((id) => doc.getElementById(id)).filter(Boolean);
}

/**
 * Mark the page behind the dialog inert (or restore it). `inert` removes the
 * subtree from the tab order AND the accessibility tree, which is what
 * aria-modal="true" promises; aria-hidden is set alongside for browsers that
 * predate the attribute. Idempotent.
 * @param {any} doc
 * @param {boolean} on
 * @returns {void}
 */
function setBackgroundInert(doc, on) {
  for (const el of inertTargets(doc)) {
    if (on) {
      el.setAttribute('inert', '');
      el.setAttribute('aria-hidden', 'true');
    } else {
      el.removeAttribute('inert');
      el.removeAttribute('aria-hidden');
    }
  }
  if (doc.body && doc.body.classList) doc.body.classList.toggle('modal-locked', on);
}
/** Caller cleanup, run by closeModal however the dialog was dismissed —
 *  footer button, Escape, or a future backdrop click. @type {(() => void)|null} */
let _onClose = null;

/**
 * Find or build the modal host: overlay #ov > dialog #modal with
 * #m-title/#m-sub/#m-body/#m-foot.
 * @param {Document} doc
 * @returns {{ov: HTMLElement, modal: HTMLElement, title: HTMLElement, sub: HTMLElement, body: HTMLElement, foot: HTMLElement}}
 */
function ensureHost(doc) {
  let ov = doc.getElementById('ov');
  if (!ov) {
    ov = doc.createElement('div');
    ov.id = 'ov';
    ov.className = 'ov';
    ov.innerHTML =
      '<div class="modal" id="modal" role="dialog" aria-modal="true" aria-labelledby="m-title" tabindex="-1">' +
      '<div class="m-head"><h3 id="m-title"></h3><p id="m-sub"></p></div>' +
      '<div id="m-body" class="m-body"></div>' +
      '<div id="m-foot" class="m-foot"></div></div>';
    doc.body.appendChild(ov);
  }
  return {
    ov,
    modal: /** @type {HTMLElement} */ (doc.getElementById('modal')),
    title: /** @type {HTMLElement} */ (doc.getElementById('m-title')),
    sub: /** @type {HTMLElement} */ (doc.getElementById('m-sub')),
    body: /** @type {HTMLElement} */ (doc.getElementById('m-body')),
    foot: /** @type {HTMLElement} */ (doc.getElementById('m-foot')),
  };
}

/**
 * Open the modal.
 * @param {string} title
 * @param {string} sub subtitle ('' hides nothing — kept for legacy parity)
 * @param {string} body HTML string (caller is responsible for esc()-ing data)
 * @param {Array<{l: string, v?: string, k?: string, fn: () => void}>} [actions]
 *        footer buttons: l label, v extra class ('pri' for primary), k stable
 *        key for setBtnBusy matching, fn click handler (a FUNCTION — no
 *        onclick strings).
 * @param {boolean} [lg] large layout
 * @param {{onClose?: () => void}} [opts] onClose runs on EVERY dismissal route —
 *        footer button, Escape, programmatic closeModal() — so a dialog whose
 *        cancel path has real cleanup (the MFA enrolment modal unenrolls the
 *        half-created factor) cannot be escaped past. It runs once.
 * @returns {void}
 */
export function openModal(title, sub, body, actions, lg = false, opts) {
  const doc = /** @type {any} */ (globalThis).document;
  if (!doc) return;
  const h = ensureHost(doc);
  _onClose = (opts && typeof opts.onClose === 'function') ? opts.onClose : null;
  h.title.textContent = title;
  h.sub.textContent = sub || '';
  h.body.innerHTML = body;

  // A11y (legacy port): associate each .field's <label> with its control so
  // screen readers announce a name; unnamed controls borrow their placeholder.
  h.body.querySelectorAll('.field').forEach((f) => {
    const lab = f.querySelector('label');
    const ctrl = f.querySelector('input,select,textarea');
    if (lab && ctrl) {
      if (!ctrl.id) ctrl.id = 'f' + Math.random().toString(36).slice(2, 8);
      if (!lab.getAttribute('for')) lab.setAttribute('for', ctrl.id);
    }
  });
  h.body.querySelectorAll('input,select,textarea').forEach((ctrl) => {
    if (ctrl.getAttribute('aria-label') || ctrl.getAttribute('aria-labelledby')) return;
    if (ctrl.id && h.body.querySelector('label[for="' + ctrl.id + '"]')) return;
    const ph = ctrl.getAttribute('placeholder');
    if (ph) ctrl.setAttribute('aria-label', ph);
  });

  h.modal.className = 'modal' + (lg ? ' lg' : '');
  // The subtitle carries real instructions ("Scan the code…, then confirm");
  // wire it as the dialog's description so it is read with the title.
  if (sub) h.modal.setAttribute('aria-describedby', 'm-sub');
  else h.modal.removeAttribute('aria-describedby');
  h.foot.innerHTML = '';
  if (actions && actions.length) {
    h.foot.style.display = 'flex';
    actions.forEach((a, i) => {
      const b = doc.createElement('button');
      b.className = 'btn ' + (a.v || '');
      b.textContent = a.l;
      b.dataset.act = a.k || String(i);
      b.addEventListener('click', a.fn);
      h.foot.appendChild(b);
    });
  } else {
    h.foot.style.display = 'none';
  }
  h.ov.classList.add('show');
  setBackgroundInert(doc, true);

  // Focus management: remember the trigger, trap Tab, move focus in.
  _prevFocus = doc.activeElement;
  // Listening on the DIALOG only caught Tab when focus was already inside it —
  // the `outside → -1` branch below was unreachable, and a keyboard user whose
  // focus was still on the page (the 30 ms auto-focus missed, or they came
  // back from the browser chrome) tabbed straight through the controls under
  // the scrim. The listener lives on the document while the dialog is open.
  if (_trapHandler) doc.removeEventListener('keydown', _trapHandler, true);
  _trapHandler = (ev) => {
    if (ev.key !== 'Tab') return;
    const focusables = [...h.modal.querySelectorAll(FOCUSABLE)].filter(
      (el) => /** @type {HTMLElement} */ (el).offsetParent !== null
    );
    const idx = focusables.indexOf(/** @type {any} */ (doc.activeElement));
    const outside = !h.modal.contains(doc.activeElement);
    const step = trapStep(ev.shiftKey, outside ? -1 : idx, focusables.length);
    if (step === null) return;
    ev.preventDefault();
    if (step === -2) h.modal.focus();
    else /** @type {HTMLElement} */ (focusables[step]).focus();
  };
  doc.addEventListener('keydown', _trapHandler, true);
  setTimeout(() => {
    if (!h.ov.classList.contains('show')) return;
    if (h.modal.contains(doc.activeElement)) return; // a caller already focused a field
    try {
      /** @type {HTMLElement} */ (h.body.querySelector('input,select,textarea,button') || h.modal).focus();
    } catch {
      /* focus best-effort */
    }
  }, 30);
}

/**
 * Close the modal (idempotent), WIPE ITS BODY, and restore focus to the trigger.
 *
 * The body wipe is a security requirement, not tidiness. `.ov` is toggled with a
 * class, so everything the dialog rendered stays live in the document after it
 * is dismissed — and the MFA enrolment dialog renders the raw TOTP seed and the
 * full `otpauth://…?secret=…` URI into two inputs. On a shared clinic terminal
 * (the reason auth.js ships a 30-minute idle logout at all) that credential sat
 * readable in DevTools until some later openModal() happened to overwrite it,
 * and unlike a session token it does not expire and cannot be revoked without
 * re-enrolling. openModal always rebuilds the body, so clearing it is free.
 * @returns {void}
 */
export function closeModal() {
  const doc = /** @type {any} */ (globalThis).document;
  if (!doc) return;
  const ov = doc.getElementById('ov');
  if (!ov) return;
  const wasOpen = ov.classList.contains('show');
  ov.classList.remove('show');
  if (_trapHandler) doc.removeEventListener('keydown', _trapHandler, true);
  _trapHandler = null;
  if (wasOpen) setBackgroundInert(doc, false);

  const body = doc.getElementById('m-body');
  if (body) body.innerHTML = '';

  // Run the caller's cleanup exactly once, and only for a dialog that was
  // actually open — closeModal() is called defensively all over the app.
  const cb = _onClose;
  _onClose = null;
  if (cb && wasOpen) {
    try {
      cb();
    } catch {
      /* cleanup is best-effort — it must never block the close */
    }
  }
  if (_prevFocus && /** @type {any} */ (_prevFocus).focus) {
    try {
      /** @type {any} */ (_prevFocus).focus();
    } catch {
      /* trigger may be gone */
    }
  }
  _prevFocus = null;
}

/**
 * Latch the open modal's footer during an async write so a slow or failed
 * save can never be double-submitted (legacy setBtnBusy port). busy=true
 * disables every footer button and renames the target — the button whose
 * data-act matches `match`, else the primary `.pri`, else the last button —
 * to `label`; busy=false restores all buttons and the original label.
 * Pair setBtnBusy(true,…) with setBtnBusy(false) on the ERROR path; on
 * success just closeModal() (which rebuilds the footer next time).
 * @param {boolean} busy
 * @param {string} [label]
 * @param {string} [match] data-act key of the target button
 * @returns {void}
 */
export function setBtnBusy(busy, label, match) {
  const doc = /** @type {any} */ (globalThis).document;
  if (!doc) return;
  const foot = doc.getElementById('m-foot');
  if (!foot) return;
  const btns = [...foot.querySelectorAll('button')];
  let t = match ? btns.find((b) => b.dataset.act === match) : null;
  if (!t) t = foot.querySelector('.pri') || btns[btns.length - 1];
  btns.forEach((b) => (b.disabled = busy));
  if (t) {
    if (busy) {
      if (t.dataset.lbl === undefined) t.dataset.lbl = t.textContent;
      t.textContent = label || 'Saving…';
    } else if (t.dataset.lbl !== undefined) {
      t.textContent = t.dataset.lbl;
      delete t.dataset.lbl;
    }
  }
}
